import { createContext, useContext } from 'react';

import type { Attachment, ForUser, Task, TaskAttributes } from 'bundles/apex/tasks/types';
import type { Parent } from 'bundles/crm/types';
import type { ApiFilters, Filters } from '../filters/types';
import type { Props as FiltersConfig } from '../filters';
import type { Props as TaskProps } from '../task';

export type Pagination = {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  pageSize: number;
};

export type TasksPaths = {
  attachments: string;
  checkForImmediateReminders: string;
  create?: string;
  formDependenciesPath: string;
  index?: string;
};

export type TasksContextValue = {
  tasks: Task[];
  loading: boolean;
  pagination: Pagination | null;
  selectedTask: Task | null;
  showModal: boolean;
  filters: Filters;
  changeFilters: (changes: Partial<Filters>, debounce?: boolean) => void;
  setFilters: (filters: ApiFilters) => void;
  setLoading: (loading: boolean) => void;
  setPage: (page: number) => Promise<Task[]>;
  setSelectedTask: (task: Task | null) => void;
  setShowModal: (show: boolean) => void;
  showTaskModal: (task?: Task) => void;
  reloadTasks: () => void;
  createTask: (task: TaskAttributes) => Promise<Task>;
  updateTask: (task: TaskAttributes) => Promise<Task>;
  deleteTask: (task: Task) => Promise<void>;
  addTaskAttachment: (task: Task, attachment: Attachment) => void;
  removeTaskAttachment: (task: Task, attachment: Attachment) => void;
  currentParent?: Parent;
  features?: TaskProps['features'];
  filtersConfig: FiltersConfig;
  forUser: ForUser;
  hideAssignees: boolean;
  options: {
    assignees?: [];
    parents?: [];
  };
  paginate: boolean;
  paths: TasksPaths;
  showParents?: TaskProps['showParents'];
  showRouterPrompt: boolean;
};

export const TasksContext = createContext<TasksContextValue | null>(null);

export function useTasks(): TasksContextValue {
  const ctx = useContext(TasksContext);
  if (!ctx) throw new Error('useTasks must be used inside <Tasks>');
  return ctx;
}
